import { FC, PropsWithChildren } from "react";

import { BaseProps } from "../../../BaseProps";
import { createComponentStyleHook } from "../../../tokens";
import { getHtmlPropsFromBaseProps } from "../../../utilities/getHtmlPropsFromBaseProps";
import { SharedInputProps } from "../../SharedInputProps";
import { useSubmitting } from "../../SubmitGroup";
import { useValidity } from "../../ValidityGroup";
import { getStyleModeSafeProps } from "../../utils/getStyleModeSafeProps";
import { Error } from "../Error";
import { Instructions } from "../Instructions";
import { Label } from "../Label";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

/**
 * The `InputContainer` component properties.
 */
export interface InputContainerProps
  extends Omit<SharedInputProps<unknown>, "value" | "defaultValue" | "autoFocus" | "tabIndex">, BaseProps {
  /**
   * The input ID.
   */
  readonly id?: string;

  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * Hides the label.
   */
  readonly hideLabel?: boolean;

  /**
   * Hides the instructions if not invalid.
   * @deprecated This is no longer needed, not setting instructions hides instructions due to layout changes.
   */
  readonly hideInstructions?: boolean;
}

const useResolvedInputContainerStyles = createComponentStyleHook<InputContainerProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "form_input_container",
});

/**
 * Component which acts as the container for all inputs.
 */
export const InputContainer: FC<PropsWithChildren<InputContainerProps>> = (props) => {
  const { id, children, label, instructions, required, disabled = false, readOnly = false, tooltip, hideLabel = false } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedInputContainerStyles(getStyleModeSafeProps(props));
  const [invalid] = useValidity();
  const submitting = useSubmitting();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.InputContainer,
      {
        disabled,
        readonly: readOnly || submitting,
        invalid: invalid,
      },
    ],
    // Keep the "stack" prefix so the container's testid stays distinct from the inner control's testid.
    testIdPrefix: "stack",
  });
  return (
    <StyleModeBoundary mode={mode}>
      <div {...baseHtmlProps}>
        {!hideLabel && (
          <Label forId={id} required={!disabled && !readOnly && required} tooltip={tooltip}>
            {label}
          </Label>
        )}
        {children}
        <Instructions forId={id}>{instructions}</Instructions>
        <Error forId={`${id}-error`} aria-live={invalid ? "assertive" : "polite"} role={invalid ? "alert" : undefined} />
      </div>
    </StyleModeBoundary>
  );
};
